import "@/index.css";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Play, Instagram, Linkedin, ArrowRight, ExternalLink, Phone, Mail, ChevronDown, ChevronUp, GripHorizontal } from "lucide-react";
import { Toaster, toast } from "sonner";
import { SiteDataProvider, useSiteData } from "./context/SiteDataContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Image optimization helper - returns optimized image URL for external images
const getOptimizedImage = (url, width = 400, quality = 75) => {
  if (!url) return url;
  // Only optimize external images (not local ones)
  if (url.startsWith('/') || url.includes('localhost')) return url;
  // Skip SVG files - they don't need optimization and cause 500 errors
  if (url.toLowerCase().includes('.svg')) return url;
  // Skip problematic external sources
  if (url.includes('wikimedia.org') || url.includes('wikipedia.org')) return url;
  return `${API}/optimize-image?url=${encodeURIComponent(url)}&width=${width}&quality=${quality}`;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => {
      window.scrollBy(0, 1);
      window.scrollBy(0, -1);
    }, 100);
  }, [pathname]);
  return null;
};

// NYZA Logo - Black version (use filter to make white logo black)
const NYZA_LOGO_URL = "/images/nyza-logo.webp";

// ============================================
// NAVIGATION - Pill Style with Active Indicator
// ============================================
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/references') return location.pathname === '/references';
    if (path === '/') return location.pathname === '/';
    return false;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 py-4 md:py-6">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="relative z-10">
            <img 
              src={NYZA_LOGO_URL} 
              alt="NYZA Studio" 
              className="h-8 md:h-10"
              style={{ filter: 'brightness(0)' }}
            />
          </Link>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center bg-white/90 backdrop-blur-md rounded-full px-2 py-1.5 shadow-md border border-gray-200">
            <Link 
              to="/"
              className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full ${isActive('/') ? 'text-gray-900 bg-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              HOME
            </Link>
            <Link 
              to="/references"
              className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full ${isActive('/references') ? 'text-gray-900 bg-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              REFERENCES
            </Link>
            <Link 
              to="/products"
              className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-600 hover:text-gray-900"
            >
              PRODUCTS
            </Link>
            <Link 
              to="/marketing"
              className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-600 hover:text-gray-900"
            >
              MARKETING
            </Link>
          </div>
          
          {/* Contact Button */}
          <a href="#contact" className="hidden md:block bg-[#9A45FF] text-white text-xs font-medium uppercase tracking-wide px-5 py-2 rounded-full hover:bg-[#8035E8] transition-colors">
            CONTACT
          </a>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-gray-900" 
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden mt-4 bg-white rounded-2xl shadow-lg border border-gray-100 p-4">
            <Link 
              to="/" 
              className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide hover:text-purple-600 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              HOME
            </Link>
            <Link 
              to="/references" 
              className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide hover:text-purple-600 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              REFERENCES
            </Link>
            <Link 
              to="/products" 
              className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide hover:text-purple-600 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              PRODUCTS
            </Link>
            <Link 
              to="/marketing" 
              className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide hover:text-purple-600 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              MARKETING
            </Link>
            <a
              href="#contact"
              className="block w-full text-center mt-3 bg-[#9A45FF] text-white text-sm font-medium uppercase tracking-wide px-4 py-2 rounded-full"
              onClick={() => setIsOpen(false)}
            >
              CONTACT
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// ============================================
// HERO SECTION - Figma Design
// ============================================
const Hero = () => {
  const { content } = useSiteData();
  
  return (
    <section className="min-h-screen flex flex-col items-center relative overflow-hidden pt-40 md:pt-48 pb-0" style={{ background: '#E3BFFF' }}>
      {/* White Blurry Cloud Circles - Balanced white with hint of purple */}
      <div className="absolute top-[-10%] left-[-10%] w-[700px] h-[700px] md:w-[1000px] md:h-[1000px] rounded-full bg-white/65 blur-[150px] md:blur-[200px]" />
      <div className="absolute top-[0%] right-[-10%] w-[600px] h-[600px] md:w-[900px] md:h-[900px] rounded-full bg-white/60 blur-[130px] md:blur-[180px]" />
      <div className="absolute bottom-[0%] left-[20%] w-[600px] h-[600px] md:w-[800px] md:h-[800px] rounded-full bg-white/55 blur-[140px] md:blur-[190px]" />
      <div className="absolute top-[30%] right-[10%] w-[500px] h-[500px] md:w-[700px] md:h-[700px] rounded-full bg-white/50 blur-[120px] md:blur-[170px]" />
      <div className="absolute top-[50%] left-[-5%] w-[550px] h-[550px] md:w-[750px] md:h-[750px] rounded-full bg-white/55 blur-[130px] md:blur-[180px]" />
      <div className="absolute top-[15%] left-[30%] w-[500px] h-[500px] md:w-[700px] md:h-[700px] rounded-full bg-white/45 blur-[110px] md:blur-[160px]" />
      
      <div className="max-w-5xl mx-auto px-4 w-full relative z-10 text-center">
        {/* Main Title - Medium weight */}
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-medium mb-3 leading-tight text-gray-900"
        >
          CUSTOM WEB SOLUTIONS &<br/>
          SOCIAL MEDIA STUFF
        </motion.h1>
        
        {/* Subtitle - Much Smaller */}
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-[10px] md:text-xs text-gray-700 mb-5 max-w-md mx-auto tracking-wide"
        >
          {content?.hero_subtitle || "IF YOU CAN DREAM IT, WE CAN CREATE IT."}
        </motion.p>
        
        {/* Buttons - Smaller */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex items-center gap-3 justify-center flex-wrap relative z-20"
        >
          <a href="#portfolio" className="bg-[#9A45FF] text-white text-[10px] md:text-xs font-medium uppercase tracking-wide px-4 py-2 rounded-full hover:bg-[#8035E8] transition-colors shadow-md">
            MORE INFOS
          </a>
          <Link to="/schedule-demo" className="bg-gray-900 text-white text-[10px] md:text-xs font-medium uppercase tracking-wide px-4 py-2 rounded-full hover:bg-gray-800 transition-colors flex items-center gap-2">
            {/* Pulsing Green Dot */}
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
            </span>
            SCHEDULE A DEMO
          </Link>
        </motion.div>
        
        {/* Tablet Mockups - 2 Stacked, one behind the other - CENTERED */}
        {/* Mobile: tablets lower, Desktop: original position */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="-mt-16 sm:-mt-24 md:-mt-52 relative w-[140%] -ml-[30%]"
        >
          {/* Back Tablet (behind, closer together) */}
          <div className="absolute left-[2%] top-[10%] w-[100%] z-0">
            <img 
              src="/images/dashboard-tablet.webp" 
              alt="Dashboard Preview Back" 
              className="w-full h-auto rounded-xl md:rounded-3xl"
            />
          </div>
          
          {/* Front Tablet (main, offset to right, higher up) */}
          <div className="relative z-10 ml-[8%] w-[100%]">
            <img 
              src="/images/dashboard-tablet.webp" 
              alt="Dashboard Preview" 
              className="w-full h-auto rounded-xl md:rounded-3xl"
            />
          </div>
        </motion.div>
      </div>
      
      {/* White Gradient Fade at Bottom - slightly higher on mobile */}
      <div className="absolute bottom-0 left-0 right-0 w-full h-[350px] md:h-[600px] bg-gradient-to-t from-white from-60% via-white/98 to-transparent z-20" />
    </section>
  );
};

// ============================================
// LOGO CAROUSEL - Infinity Scroll
// ============================================
const LogoCarousel = () => {
  const { logos } = useSiteData();
  
  if (!logos || logos.length === 0) return null;
  
  // Triple logos for seamless infinite scroll
  const allLogos = [...logos, ...logos, ...logos];
  
  return (
    <section className="py-4 md:py-6 bg-white relative overflow-hidden -mt-40 md:-mt-56 z-30">
      <div className="max-w-6xl mx-auto px-4">
        <p className="text-center text-gray-900 text-xs font-medium uppercase tracking-widest mb-6">
          If they trust us, you can too
        </p>
      </div>
      
      {/* Box-style carousel with short fade edges */}
      <div className="max-w-6xl mx-auto px-4">
        <div className="relative">
          {/* Left fade gradient - short */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          {/* Right fade gradient - short */}
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          
          <div className="overflow-hidden">
            <div 
              className="flex items-center gap-12 md:gap-16 logo-scroll-animation"
              style={{
                width: 'fit-content',
                '--scroll-duration': `${logos.length * 5}s`,
              }}
            >
              {allLogos.map((logo, index) => (
                <div 
                  key={`logo-${index}`} 
                  className="flex-shrink-0 h-6 md:h-8 flex items-center justify-center"
                >
                  <img 
                    src={logo.url} 
                    alt={logo.name} 
                    className="h-full w-auto object-contain"
                    style={{ maxWidth: '100px', minWidth: '60px', filter: 'brightness(0)' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// REFERENCES SECTION - Embla Carousel with Badges
// ============================================
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// YouTube Player Component with pause detection
const YouTubePlayer = ({ videoId, onClose, onVideoEnd }) => {
  const playerRef = useRef(null);
  const attachRef = useRef(null);
  const iframeId = useRef(`yt-${videoId}-${Date.now()}`).current;

  useEffect(() => {
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
    return () => {
      clearInterval(attachRef.current);
      playerRef.current = null;
    };
  }, []);

  const handleIframeLoad = () => {
    attachRef.current = setInterval(() => {
      if (window.YT?.Player && !playerRef.current) {
        clearInterval(attachRef.current);
        try {
          playerRef.current = new window.YT.Player(iframeId, {
            events: {
              onStateChange: (e) => {
                // 0 = ended, 2 = paused
                if (e.data === 0 || e.data === 2) {
                  onVideoEnd?.();
                  onClose();
                }
              },
            },
          });
        } catch (err) {
          console.warn('YT attach failed:', err);
        }
      }
    }, 300);
  };

  return (
    <div className="absolute inset-0 bg-black z-20">
      <iframe
        id={iframeId}
        onLoad={handleIframeLoad}
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&playsinline=1&rel=0&modestbranding=1&enablejsapi=1`}
        className="w-full h-full"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        frameBorder="0"
      />
      <button 
        onClick={(e) => { e.stopPropagation(); onVideoEnd?.(); onClose(); }}
        className="absolute top-2 right-2 bg-black/80 text-white p-2 rounded-full hover:bg-purple-600 transition-colors z-30"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const ReferencesCarousel = () => {
  const { featuredReferences } = useSiteData();
  const social = featuredReferences;
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [playingYoutubeId, setPlayingYoutubeId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showDragHint, setShowDragHint] = useState(false);
  
  // Animation state
  const [xPos, setXPos] = useState(0);
  const isPaused = useRef(false);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStartX = useRef(0);
  const dragStartPos = useRef(0);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const itemWidth = isMobile ? 200 : 280;
  const gap = 20;
  const singleSetWidth = social.length * (itemWidth + gap);
  const speed = 0.03; // pixels per ms (slower)

  // Animation loop - GPU optimized
  useEffect(() => {
    if (!social || social.length === 0) return;

    const animate = (timestamp) => {
      if (isPaused.current || playingVideoId) {
        lastTimeRef.current = null;
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      if (lastTimeRef.current === null) {
        lastTimeRef.current = timestamp;
      }

      const deltaTime = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      setXPos(prev => {
        let newX = prev - (deltaTime * speed);
        // Reset when scrolled past one set
        if (newX <= -singleSetWidth) {
          newX += singleSetWidth;
        }
        return newX;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [social?.length, singleSetWidth, playingVideoId]);

  const handlePlayVideo = (uniqueKey, youtubeId) => {
    if (hasDragged.current) return;
    setPlayingVideoId(uniqueKey);
    setPlayingYoutubeId(youtubeId);
  };

  const handleCloseVideo = () => {
    setPlayingVideoId(null);
    setPlayingYoutubeId(null);
  };

  // Mouse events
  const handleMouseEnter = () => {
    if (!playingVideoId) {
      isPaused.current = true;
      setShowDragHint(true);
    }
  };

  const handleMouseLeave = () => {
    if (!playingVideoId) {
      isPaused.current = false;
      isDragging.current = false;
    }
    setShowDragHint(false);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.clientX;
    dragStartPos.current = xPos;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const diff = e.clientX - dragStartX.current;
    if (Math.abs(diff) > 5) hasDragged.current = true;
    
    let newX = dragStartPos.current + diff;
    // Keep within bounds
    if (newX > 0) newX = newX % singleSetWidth - singleSetWidth;
    if (newX <= -singleSetWidth) newX = newX % singleSetWidth;
    setXPos(newX);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    setTimeout(() => { hasDragged.current = false; }, 100);
  };

  // Touch events
  const handleTouchStart = (e) => {
    isDragging.current = true;
    hasDragged.current = false;
    isPaused.current = true;
    dragStartX.current = e.touches[0].clientX;
    dragStartPos.current = xPos;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const diff = e.touches[0].clientX - dragStartX.current;
    if (Math.abs(diff) > 5) hasDragged.current = true;
    
    let newX = dragStartPos.current + diff;
    if (newX > 0) newX = newX % singleSetWidth - singleSetWidth;
    if (newX <= -singleSetWidth) newX = newX % singleSetWidth;
    setXPos(newX);
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    isPaused.current = false;
    setTimeout(() => { hasDragged.current = false; }, 100);
  };

  if (!social || social.length === 0) return null;

  const displayItems = [...social, ...social, ...social];

  return (
    <section id="portfolio" className="pt-32 md:pt-40 pb-20 bg-white relative overflow-hidden z-30">
      <div className="blur-circle-soft absolute top-0 right-0 opacity-50" />
      
      <div className="max-w-6xl mx-auto px-4 mb-12">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex justify-between items-end"
        >
          <div>
            <h2 className="text-3xl md:text-4xl font-normal text-gray-900 mb-4">
              TAKE A LOOK AT<br/>OUR REFERENCES
            </h2>
            <p className="text-gray-500 max-w-xl text-sm">
              WE OFFER HIGH-QUALITY 2D & 3D ANIMATIONS, VIDEO PRODUCTION, PHOTOSHOOTS, GRAPHIC DESIGN, AND CUSTOM WEB SOLUTIONS — TAILORED TO BRING YOUR BRAND TO LIFE.
            </p>
          </div>
          <Link to="/references" className="hidden md:flex items-center gap-2 text-purple-600 font-medium hover:gap-3 transition-all">
            SEE ALL REFERENCES <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>

      <div className="max-w-6xl mx-auto px-4">
        <div 
          ref={containerRef}
          className={`relative overflow-hidden select-none ${isDragging.current ? 'cursor-grabbing' : 'cursor-grab'}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Drag hint overlay */}
          {!isMobile && showDragHint && !playingVideoId && !isDragging.current && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
              <div className="bg-white/90 backdrop-blur-sm text-gray-900 px-4 py-2 rounded-full flex items-center gap-2 text-sm font-medium shadow-lg border border-purple-200">
                <GripHorizontal className="w-4 h-4 text-purple-600" />
                Click and drag to scroll
              </div>
            </div>
          )}

          <div 
            className="flex"
            style={{ 
              gap: `${gap}px`,
              width: 'fit-content',
              transform: `translate3d(${xPos}px, 0, 0)`,
              willChange: 'transform'
            }}
          >
            {displayItems.map((post, index) => {
              const videoId = getYouTubeVideoId(post.youtube_url);
              const uniqueKey = `${post.id}-${index}`;
              const isPlaying = playingVideoId === uniqueKey;
              const hasVideo = post.is_video && videoId;
              
              return (
                <div 
                  key={uniqueKey}
                  className="flex-shrink-0 group"
                  style={{ width: `${itemWidth}px` }}
                  onClick={() => {
                    if (!isPlaying && !hasDragged.current && hasVideo) {
                      handlePlayVideo(uniqueKey, videoId);
                    }
                  }}
                >
                  <div className={`aspect-[9/16] rounded-2xl overflow-hidden relative bg-gray-100 shadow-lg hover:shadow-xl transition-shadow duration-300 ${hasVideo ? 'cursor-pointer' : ''}`}>
                    <img 
                      src={getOptimizedImage(post.image, 400, 75)} 
                      alt={post.profile} 
                      className={`w-full h-full object-cover transition-transform duration-700 ${isPlaying ? 'opacity-0' : 'group-hover:scale-105'}`}
                      draggable={false}
                      loading="lazy"
                    />
                    
                    {isPlaying && playingYoutubeId && (
                      <YouTubePlayer 
                        videoId={playingYoutubeId} 
                        onClose={handleCloseVideo}
                        onVideoEnd={handleCloseVideo}
                      />
                    )}
                    
                    {!isPlaying && (
                      <div className="absolute bottom-3 left-3 right-3">
                        <div className="reference-badge">
                          <div className="reference-badge-profile">
                            <img src={getOptimizedImage(post.logo, 60, 80)} alt={post.profile} loading="lazy" />
                          </div>
                          <div className="reference-badge-info flex-1 min-w-0">
                            <div className="reference-badge-name">
                              @{post.profile}
                              {post.verified && (
                                <svg className="w-2.5 h-2.5 text-purple-600 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                </svg>
                              )}
                            </div>
                            <div className="reference-badge-followers">{post.followers} · {post.category?.replace(/\s+/g, '-')}</div>
                          </div>
                          {hasVideo && (
                            <button 
                              className="reference-badge-play"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!hasDragged.current) handlePlayVideo(uniqueKey, videoId);
                              }}
                            >
                              <Play fill="currentColor" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="md:hidden text-center mt-8">
        <Link to="/references" className="inline-flex items-center gap-2 text-purple-600 font-medium">
          SEE ALL REFERENCES <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};

// ============================================
// INFINITE MARQUEE TEXT
// ============================================
const InfiniteMarquee = ({ text, speed = 30 }) => {
  const { content } = useSiteData();
  const marqueeText = content?.marquee_text || text || "AGENCYOS · CARDEALEROS · TURKOS · GYMOS · NUV MARATOS · ";
  
  return (
    <div className="py-8 bg-white overflow-hidden border-y border-gray-100">
      <div 
        className="flex whitespace-nowrap marquee-animation"
        style={{ '--marquee-duration': `${speed}s` }}
      >
        {[...Array(4)].map((_, i) => (
          <span key={i} className="text-5xl md:text-7xl lg:text-8xl font-normal text-gray-900 mx-4">
            {marqueeText}
          </span>
        ))}
      </div>
    </div>
  );
};

// ============================================
// PRODUCTS SECTION - Figma Design
// ============================================
const ProductsSection = () => {
  const products = ["CARDEALEROS", "AGENCYOS", "CLUBOS", "HOMEOS", "RESTAURANTOS", "SOCIALOS", "& MUCH MORE IS COMING"];
  const allProducts = [...products, ...products, ...products, ...products];
  
  return (
    <section id="products" className="relative pt-16 pb-24 md:pt-0 md:pb-0" style={{ paddingTop: undefined, paddingBottom: undefined, background: 'linear-gradient(180deg, #FFFFFF 0%, #E8D4FF 30%, #9A45FF 100%)' }}>
      {/* Desktop padding via style, Mobile via className */}
      <style>{`
        @media (min-width: 768px) {
          #products { padding-top: 250px !important; padding-bottom: 500px !important; }
        }
      `}</style>
      
      {/* Title section */}
      <div className="max-w-6xl mx-auto px-4 text-center mb-6 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mb-2">
            OUR PRODUCTS:<br/>
            ONE MONTHLY PRICE, FOR ALL.
          </h2>
          <p className="text-gray-500 text-sm">
            (PS: YOU CAN ALSO CUSTOMIZE OUR SOLUTIONS)
          </p>
        </motion.div>
      </div>

      {/* Monitor - scaled 2.3 with bottom mask - ORIGINAL DESKTOP VERSION */}
      <div className="max-w-5xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          {/* Blur hemisphere behind */}
          <div 
            className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[400px] md:w-[1000px] md:h-[500px] rounded-t-full -z-10"
            style={{ 
              background: 'radial-gradient(ellipse at center bottom, rgba(255,255,255,0.8) 0%, rgba(232,212,255,0.4) 50%, transparent 70%)',
              filter: 'blur(60px)'
            }}
          />
          
          {/* Monitor - smaller on mobile, large on desktop */}
          <div>
            {/* Mobile: slightly larger but fully visible */}
            <img 
              src="/images/cardealeros-monitor.webp" 
              alt="Product Preview" 
              className="w-full h-auto md:hidden"
              style={{ transform: 'scale(1.5)', transformOrigin: 'top center' }}
            />
            {/* Desktop: scale 2.3 */}
            <img 
              src="/images/cardealeros-monitor.webp" 
              alt="Product Preview" 
              className="w-full h-auto hidden md:block"
              style={{ transform: 'scale(2.3)', transformOrigin: 'top center' }}
            />
          </div>
        </motion.div>
      </div>

      {/* Bubble Bar - at very bottom, overlapping into next section */}
      <div className="absolute bottom-0 left-0 right-0 z-50 translate-y-1/2">
        <div className="max-w-4xl mx-auto px-4">
          <div 
            className="flex items-center bg-white rounded-full overflow-hidden h-14 md:h-16"
            style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 10px 30px -5px rgba(154, 69, 255, 0.3)' }}
          >
            {/* Left: Infinity carousel with product names */}
            <div className="flex-1 overflow-hidden h-full flex items-center px-4">
              <div className="relative overflow-hidden w-full">
                <div 
                  className="flex items-center whitespace-nowrap product-scroll-animation"
                  style={{ '--product-scroll-duration': '25s' }}
                >
                  {allProducts.map((product, index) => (
                    <span key={index} className="flex items-center text-sm md:text-base font-medium text-gray-900 uppercase tracking-wide">
                      {product}
                      <span className="mx-3 w-2 h-2 bg-gray-900 rounded-full inline-block flex-shrink-0"></span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Right: Purple price badge */}
            <div className="flex-shrink-0 bg-[#9A45FF] text-white px-6 md:px-10 h-full flex items-center justify-center rounded-full gap-2">
              <span className="text-[10px] md:text-xs font-medium uppercase tracking-wide">Starting at</span>
              <span className="text-2xl md:text-3xl font-bold">99€</span>
              <span className="text-[10px] md:text-xs font-medium uppercase tracking-wide">/per month</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// MARKETING BENTO GRID - 1:1 Figma Design
// ============================================
const MarketingBentoGrid = () => {
  return (
    <section id="marketing" className="pb-20 bg-white relative overflow-hidden" style={{ paddingTop: '160px' }}>
      <div className="blur-circle-soft absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-30" />
      
      <div className="max-w-6xl mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-medium text-gray-900 leading-tight">
            MARKETING THAT
          </h2>
          <h2 className="text-3xl md:text-4xl font-medium gradient-text mb-4 leading-tight">
            HITS!
          </h2>
          <p className="text-gray-500 text-sm max-w-xl">
            YOUR LIKE THE MARKETING SIDE OF OUR AGENCY? HERE SOME INFORMATION ABOUT WHAT WE OFFER THERE. IN GENERAL, WE ONLY OFFER SERVICES THAT WE CAN DELIVER TO PERFECTION.
          </p>
        </motion.div>

        {/* Bento Grid - Same layout on all screens, scaled to fit */}
        <div className="w-full">
          <div className="grid grid-cols-4 gap-1.5 md:gap-4">
            
            {/* Cell 1: NYZA Logo */}
            <div 
              className="aspect-square rounded-xl md:rounded-2xl overflow-hidden flex items-center justify-center p-2 md:p-4"
              style={{ background: '#9A45FF' }}
            >
              <img 
                src="/images/nyza-logo.webp" 
                alt="NYZA Studio" 
                className="w-full h-auto max-w-[80%] object-contain"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
            
            {/* Cell 2: FULL SOCIAL MARKETING + iPhone */}
            <div 
              className="col-span-2 rounded-xl md:rounded-2xl overflow-hidden relative md:min-h-[220px]"
              style={{ background: 'linear-gradient(135deg, #C4A1FF 0%, #E8D4FF 100%)' }}
            >
              <div className="absolute bottom-2 left-2 md:bottom-6 md:left-6 z-10">
                <h3 className="text-[8px] md:text-2xl font-normal text-gray-900 leading-tight">
                  FULL<br/>SOCIAL<br/>MARKETING
                </h3>
              </div>
              <div className="absolute right-[-150%] bottom-[-220%] w-[300%]">
                <img 
                  src="/images/social-phone.webp" 
                  alt="Social Media Phone" 
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
            
            {/* Cell 3: Person with Sunglasses (spans 2 rows) */}
            <div 
              className="row-span-2 rounded-xl md:rounded-2xl overflow-hidden"
            >
              <img 
                src="/images/person-sunglasses.jpg" 
                alt="Creative Person" 
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Cell 4: READY FOR THE NEXT STEP? */}
            <div 
              className="aspect-square rounded-xl md:rounded-2xl overflow-hidden flex items-center p-2 md:p-6"
              style={{ background: '#E8E0F0' }}
            >
              <h3 className="text-[8px] md:text-2xl font-normal text-gray-900 leading-tight">
                READY<br/>FOR THE<br/>NEXT<br/>STEP?
              </h3>
            </div>
            
            {/* Cell 5: Person with Can */}
            <div 
              className="aspect-square rounded-xl md:rounded-2xl overflow-hidden"
            >
              <img 
                src="/images/person-can.webp" 
                alt="Product Shot" 
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Cell 6: TAILORED TO + YOUR AUDIENCE with Target (spans 2 rows) */}
            <div 
              className="row-span-2 rounded-xl md:rounded-2xl overflow-visible relative flex flex-col"
              style={{ background: '#E8E0F0' }}
            >
              <div className="p-2 md:p-6">
                <h3 className="text-[8px] md:text-lg font-normal text-gray-900 leading-tight">
                  TAILORED<br/>TO
                </h3>
              </div>
              <div className="flex-1 relative flex items-center justify-center overflow-visible">
                <img 
                  src="/images/target-bento.webp" 
                  alt="Target" 
                  className="w-[160%] max-w-none h-auto object-contain absolute -bottom-2 md:-bottom-8"
                />
              </div>
              <div className="p-2 md:p-6 mt-auto">
                <h3 className="text-[8px] md:text-xl font-normal text-gray-900 leading-tight">
                  YOUR<br/>AUDIENCE
                </h3>
              </div>
            </div>
            
            {/* Cell 7: 99% SATISFACTION with Trophy (spans 2 cols) */}
            <div 
              className="col-span-2 rounded-xl md:rounded-2xl overflow-hidden relative flex items-end p-2 md:p-6 min-h-[80px] md:min-h-[180px]"
              style={{ background: 'linear-gradient(135deg, #9A45FF 0%, #B366FF 100%)' }}
            >
              <div className="absolute left-[-10%] bottom-[-25%] w-[70%]">
                <img 
                  src="/images/trophy-3d.webp" 
                  alt="Trophy" 
                  className="w-full h-auto object-contain"
                />
              </div>
              <div className="ml-auto text-right text-white z-10">
                <span className="text-xl md:text-6xl font-bold block leading-none">99%</span>
                <span className="text-[6px] md:text-xl font-bold block">SATISFACTION.</span>
                <span className="text-[5px] md:text-sm opacity-80">(WE PROMISE)</span>
              </div>
            </div>
            
            {/* Cell 8: WE GROW BRANDS */}
            <div 
              className="aspect-square rounded-xl md:rounded-2xl overflow-hidden flex items-end justify-start p-2 md:p-6"
              style={{ background: 'linear-gradient(135deg, #9A45FF 0%, #C77DFF 100%)' }}
            >
              <h3 className="text-[8px] md:text-2xl font-normal text-white leading-tight text-left">
                WE<br/>GROW<br/>BRANDS.
              </h3>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// FAQ SECTION - Almost white background
// ============================================
const FAQSection = () => {
  const { faqs } = useSiteData();
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="faq" className="py-20 relative bg-white">
      <div className="max-w-6xl mx-auto px-4">
        {/* FAQ Title - left aligned with line */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h2 className="text-4xl md:text-5xl font-medium text-gray-900 text-left mb-4">FAQ</h2>
          <div className="w-full h-[2px] bg-gray-900"></div>
        </motion.div>

        {/* FAQ Items - right aligned with PROPER SPACING */}
        <div className="flex flex-col items-end gap-6">
          {faqs.map((faq, index) => (
            <div
              key={faq.id || index}
              className="rounded-xl overflow-hidden w-full max-w-2xl shadow-md"
            >
              <button 
                className="w-full text-left px-6 py-4 flex items-center justify-between text-white font-medium text-base md:text-lg uppercase tracking-wide"
                style={{ backgroundColor: '#9A45FF' }}
                onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
              >
                <span>{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 flex-shrink-0" />
                )}
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div 
                      className="px-6 py-4 text-gray-900 text-sm md:text-base uppercase tracking-wide"
                      style={{ backgroundColor: '#E9E4FA' }}
                    >
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// CONTACT SECTION - Purple gradient background
// ============================================
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    company: '',
    budget: '',
    message: '',
    privacy_accepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.privacy_accepted) {
      toast.error('Bitte akzeptiere die Datenschutzerklärung');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: `${formData.name} ${formData.surname}`,
        email: formData.email,
        company: formData.company,
        service: formData.budget || 'Not specified',
        message: formData.message,
        privacy_accepted: formData.privacy_accepted
      });
      toast.success("Nachricht gesendet!");
      setFormData({
        name: '', surname: '', email: '', phone: '',
        company: '', budget: '', message: '', privacy_accepted: false
      });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Fehler beim Senden.';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F5F0FF 30%, #E8D4FF 70%, #D4B8FF 100%)' }}>
      {/* Purple blur circles */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-purple-300/30 blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-purple-400/20 blur-[120px]" />
      
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <div className="blur-circle-purple absolute -top-10 -left-10 w-[300px] h-[300px] opacity-40" />
              <img 
                src="/images/contact.jpg" 
                alt="Contact" 
                className="rounded-2xl shadow-2xl relative z-10 w-full object-cover"
                loading="lazy"
              />
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              SHOOT US<br/>
              <span className="gradient-text">AN MESSAGE</span>
            </h2>
            <p className="text-gray-500 text-sm mb-4">
              Ready to take your brand to the next level? Get in touch and let's build something that truly performs.
            </p>
            
            {/* Contact Info */}
            <div className="flex flex-wrap gap-4 mb-6">
              <a 
                href="mailto:workwith@nyza-studio.at" 
                className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 transition-colors"
              >
                <Mail className="w-4 h-4" />
                workwith@nyza-studio.at
              </a>
              <a 
                href="https://wa.me/message/QRDZ3NTL345SA1" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 transition-colors"
              >
                <Phone className="w-4 h-4" />
                +43 660 5502578
              </a>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="NAME"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input-light"
                />
                <input
                  type="text"
                  placeholder="SURNAME"
                  value={formData.surname}
                  onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                  className="input-light"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="email"
                  placeholder="E-MAIL"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="input-light"
                />
                <input
                  type="tel"
                  placeholder="NUMBER"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-light"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="COMPANY"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="input-light"
                />
                <input
                  type="text"
                  placeholder="BUDGET"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className="input-light"
                />
              </div>
              <textarea
                placeholder="TYPE YOUR TEXT HERE"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                className="input-light w-full min-h-[120px] resize-none"
              />
              
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={formData.privacy_accepted}
                  onChange={(e) => setFormData({ ...formData, privacy_accepted: e.target.checked })}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="privacy" className="text-sm text-gray-500">
                  Ich akzeptiere die <Link to="/datenschutz" className="text-purple-600 underline">Datenschutzerklärung</Link>.
                </label>
              </div>

              <div className="flex justify-end">
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="btn-primary flex items-center gap-3 disabled:opacity-50"
                >
                  {isSubmitting ? 'SENDING...' : 'SEND'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// FOOTER - Purple gradient continues from contact
// ============================================
const Footer = () => {
  const { content } = useSiteData();
  
  return (
    <footer className="py-12 relative" style={{ background: 'linear-gradient(180deg, #D4B8FF 0%, #B88FFF 100%)' }}>
      <div className="max-w-6xl mx-auto px-4">
        {/* Social Icons - centered at top */}
        <div className="flex items-center justify-center gap-8 mb-8">
          {content?.footer_instagram && (
            <a 
              href={content.footer_instagram} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gray-800 hover:text-purple-900 transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="w-6 h-6" />
            </a>
          )}
          {content?.footer_linkedin && (
            <a 
              href={content.footer_linkedin} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gray-800 hover:text-purple-900 transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-6 h-6" />
            </a>
          )}
          {content?.footer_youtube && (
            <a 
              href={content.footer_youtube} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gray-800 hover:text-purple-900 transition-colors"
              aria-label="YouTube"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
          )}
        </div>
        
        {/* Legal Links - centered - using anchor tags with onClick for reliable navigation */}
        <div className="flex items-center justify-center gap-8 mb-6 text-sm">
          <a 
            href="/impressum" 
            onClick={(e) => { e.preventDefault(); window.location.href = '/impressum'; }}
            className="text-gray-800 hover:text-purple-900 transition-colors uppercase font-medium cursor-pointer"
          >
            IMPRINT
          </a>
          <a 
            href="/agb" 
            onClick={(e) => { e.preventDefault(); window.location.href = '/agb'; }}
            className="text-gray-800 hover:text-purple-900 transition-colors uppercase font-medium cursor-pointer"
          >
            AGBS
          </a>
          <a 
            href="/datenschutz" 
            onClick={(e) => { e.preventDefault(); window.location.href = '/datenschutz'; }}
            className="text-gray-800 hover:text-purple-900 transition-colors uppercase font-medium cursor-pointer"
          >
            PRIVACY POLICY
          </a>
        </div>
        
        {/* Copyright - centered at bottom */}
        <p className="text-sm text-gray-800 text-center">
          © {new Date().getFullYear()} {content?.footer_copyright || 'NYZA Studio'}
        </p>
      </div>
    </footer>
  );
};

// ============================================
// COOKIE BANNER - DSGVO Compliant
// ============================================
const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = { essential: true, analytics: true, marketing: true };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const acceptSelected = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
  };

  const declineAll = () => {
    const onlyEssential = { essential: true, analytics: false, marketing: false };
    localStorage.setItem('cookieConsent', JSON.stringify(onlyEssential));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className="fixed bottom-6 right-6 z-50 max-w-sm"
    >
      <div className="cookie-banner">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-bold text-gray-900">Cookie Einstellungen</h3>
          <button 
            onClick={declineAll}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          Wir verwenden Cookies, um deine Erfahrung zu verbessern.{' '}
          <Link to="/datenschutz" className="text-purple-600 underline">
            Mehr erfahren
          </Link>
        </p>

        {showSettings && (
          <div className="space-y-3 mb-4">
            <label className="flex items-center gap-3">
              <input 
                type="checkbox" 
                checked={preferences.essential} 
                disabled 
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-gray-700">Essentiell (erforderlich)</span>
            </label>
            <label className="flex items-center gap-3">
              <input 
                type="checkbox" 
                checked={preferences.analytics}
                onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-purple-600"
              />
              <span className="text-sm text-gray-700">Analyse</span>
            </label>
            <label className="flex items-center gap-3">
              <input 
                type="checkbox" 
                checked={preferences.marketing}
                onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-purple-600"
              />
              <span className="text-sm text-gray-700">Marketing</span>
            </label>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <button onClick={acceptAll} className="btn-primary w-full py-2 text-sm">
            ALLE AKZEPTIEREN
          </button>
          <div className="flex gap-2">
            <button 
              onClick={() => setShowSettings(!showSettings)} 
              className="flex-1 btn-outline py-2 text-sm"
            >
              {showSettings ? 'VERBERGEN' : 'EINSTELLUNGEN'}
            </button>
            {showSettings && (
              <button onClick={acceptSelected} className="flex-1 btn-secondary py-2 text-sm">
                AUSWAHL SPEICHERN
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// MAIN APP
// ============================================
const AppContent = () => {
  const { loading } = useSiteData();

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        window.scrollBy(0, 1);
        window.scrollBy(0, -1);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full"
      />
    </div>
  );

  return (
    <motion.div 
      className="bg-white min-h-screen relative overflow-x-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Toaster position="top-center" richColors />
      <ScrollToTop />
      
      <Navigation />
      
      <main>
        <Hero />
        <LogoCarousel />
        <ReferencesCarousel />
        <InfiniteMarquee />
        <ProductsSection />
        <MarketingBentoGrid />
        <FAQSection />
        <ContactSection />
      </main>
      
      <Footer />
      <CookieBanner />
    </motion.div>
  );
};

function App() {
  return (
    <SiteDataProvider>
      <AppContent />
    </SiteDataProvider>
  );
}

export default App;
